import { Flex, Spin } from 'antd'
import isEmpty from 'lodash-es/isEmpty'
import { FunctionComponent } from 'react'
import styled, { useTheme } from 'styled-components'
import { ApexCharts } from '@/components/common/apex-charts'
import Blur from '@/components/common/blur'
import KolDetailDataBlockSubHeader from '@/components/kol/common/kol-detail-data-block-sub-header'
import { NoData } from '@/components/kol/detail/no-data'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Audience } from '@/store/kol-detail'

const getGenderRatio = (value: number): number => {
  return parseFloat(value.toFixed(2))
}

interface AudienceAgeGenderChartProps {
  audienceData?: Audience
  blur: boolean
  isFetching: boolean
}

const AudienceAgeGenderChart: FunctionComponent<
  AudienceAgeGenderChartProps
> = ({ audienceData, blur, isFetching }) => {
  const { formatMessage, formatNumber } = useIntl()
  const theme = useTheme()

  const gendersPerAges = audienceData?.gendersPerAges ?? []

  const series = [
    {
      name: formatMessage({
        id: 'kol:audience_analysis_gender_male',
      }),
      color: theme.colors.chart.blue3,
      data: gendersPerAges.map((gender) => {
        return {
          x: gender.code,
          y: getGenderRatio(gender.male),
        }
      }),
    },
    {
      name: formatMessage({
        id: 'kol:audience_analysis_gender_female',
      }),
      color: theme.colors.chart.red3,
      data: gendersPerAges.map((gender) => {
        return {
          x: gender.code,
          y: getGenderRatio(gender.female),
        }
      }),
    },
  ]

  const options = {
    chart: {
      id: 'AudienceAgeGenderChart',
      stacked: true,
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top',
          total: {
            enabled: true,
            style: {
              color: theme.colors.text.primary,
              fontSize: '12px',
              fontWeight: 400,
            },
            formatter: (val: number): string => {
              return formatNumber(val, { isPercentageDisplay: true })
            },
          },
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (val: number): string => {
          return formatNumber(val, { isPercentageDisplay: true })
        },
      },
    },
    tooltip: {
      y: {
        formatter: (value: number): string =>
          formatNumber(value, { isPercentageDisplay: true }),
      },
    },
    legend: {
      position: 'right',
      horizontalAlign: 'left',
    },
  }

  return (
    <Wrapper>
      <KolDetailDataBlockSubHeader
        title={formatMessage({ id: 'kol:audience_analysis_age_ratio' })}
      />
      {isFetching ? (
        <Flex align='center' justify='center' style={{ minHeight: 140 }}>
          <Spin />
        </Flex>
      ) : (
        <Blur activate={blur}>
          {isEmpty(gendersPerAges) ? (
            <NoData />
          ) : (
            <ApexCharts options={options} series={series} type='bar' />
          )}
        </Blur>
      )}
    </Wrapper>
  )
}

export default AudienceAgeGenderChart

const Wrapper = styled.div`
  ${KolDetailDataBlockSubHeader} {
    margin: 0 0 15px 0;
  }
`
